/**
 * JLPT 考试特定的题目类型定义
 */

import {
  QuestionType,
  createExamQuestionTypeDefinition,
  createFieldDefinition,
  type ExamSpecificQuestionTypeDefinition,
} from '@examflow/core';

/**
 * JLPT 等级字段（N5-N1，三个部分共用）
 */
const jlptLevelField = createFieldDefinition(
  'jlptLevel',
  'string',
  true,
  'JLPT 等级 (N5-N1)',
  {
    validator: (value) => {
      const validLevels = ['N5', 'N4', 'N3', 'N2', 'N1'];
      return validLevels.includes(value as string)
        ? true
        : `JLPT 等级必须是以下之一: ${validLevels.join(', ')}`;
    },
  }
);

/**
 * JLPT 语言知识（文字·词汇）题定义
 */
export const jlptVocabularyDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'jlpt_vocabulary',
    QuestionType.SINGLE_CHOICE,
    'JLPT 文字·词汇题',
    {
      extraFields: {
        // JLPT 等级（N5-N1）
        jlptLevel: jlptLevelField,
        // 具体题型
        questionFormat: createFieldDefinition(
          'questionFormat',
          'string',
          true,
          'JLPT 文字·词汇题型格式',
          {
            validator: (value) => {
              const validFormats = [
                'kanji_reading', // 汉字读法
                'orthography', // 表记（汉字书写）
                'word_formation', // 词语构成
                'contextually_defined_expressions', // 根据语境选词
                'paraphrases', // 近义词替换
                'usage', // 词汇用法
              ];
              return validFormats.includes(value as string)
                ? true
                : `不支持的 JLPT 文字·词汇题型: ${value}，必须是以下之一: ${validFormats.join(', ')}`;
            },
          }
        ),
      },

      constraints: {
        // JLPT 文字·词汇题选项通常是 4 个
        expectedOptionCount: 4,
      },

      customValidator: (question) => {
        const errors = [];

        // 验证选项数量
        if (question.options && question.options.length !== 4) {
          errors.push({
            field: 'options',
            message: 'JLPT 文字·词汇题通常有 4 个选项',
            code: 'JLPT_VOCABULARY_OPTIONS_COUNT',
          });
        }

        return errors;
      },
    }
  );

/**
 * JLPT 语言知识（语法）·阅读题定义
 */
export const jlptGrammarReadingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'jlpt_grammar_reading',
    QuestionType.SINGLE_CHOICE,
    'JLPT 语法·阅读题',
    {
      requiredStimulus: ['passage'],
      extraFields: {
        // JLPT 等级（N5-N1）
        jlptLevel: jlptLevelField,
        // 具体题型
        questionFormat: createFieldDefinition(
          'questionFormat',
          'string',
          true,
          'JLPT 语法·阅读题型格式',
          {
            validator: (value) => {
              const validFormats = [
                'grammar_selection', // 语法形式选择
                'sentence_composition', // 句子排序组合
                'text_grammar', // 文章语法（完形填空）
                'short_reading', // 短篇阅读
                'mid_reading', // 中篇阅读
                'long_reading', // 长篇阅读
              ];
              return validFormats.includes(value as string)
                ? true
                : `不支持的 JLPT 语法·阅读题型: ${value}，必须是以下之一: ${validFormats.join(', ')}`;
            },
          }
        ),
      },

      constraints: {
        maxWordCount: 1500, // 长篇阅读材料上限约 1500 词
      },

      customValidator: (question) => {
        const errors = [];

        // 验证阅读材料长度
        if (question.passage) {
          const wordCount = question.passage.split(/\s+/).length;
          if (wordCount > 1500) {
            errors.push({
              field: 'passage',
              message: 'JLPT 阅读材料不能超过 1500 词',
              code: 'JLPT_PASSAGE_TOO_LONG',
            });
          }
        }

        return errors;
      },
    }
  );

/**
 * JLPT 听力题定义
 */
export const jlptListeningDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'jlpt_listening',
    QuestionType.SINGLE_CHOICE,
    'JLPT 听力题',
    {
      requiredStimulus: ['audio'],
      extraFields: {
        // JLPT 等级（N5-N1）
        jlptLevel: jlptLevelField,
        // 具体题型
        questionFormat: createFieldDefinition(
          'questionFormat',
          'string',
          true,
          'JLPT 听力题型格式',
          {
            validator: (value) => {
              const validFormats = [
                'task_comprehension', // 课题理解
                'point_comprehension', // 要点理解
                'summary_comprehension', // 概要理解
                'utterance_expressions', // 语言表达（即时应答前的话语表达）
                'quick_response', // 即时应答
                'integrated_comprehension', // 综合理解
              ];
              return validFormats.includes(value as string)
                ? true
                : `不支持的 JLPT 听力题型: ${value}，必须是以下之一: ${validFormats.join(', ')}`;
            },
          }
        ),
        // 音频时长（秒）
        audioDuration: createFieldDefinition(
          'audioDuration',
          'number',
          false,
          '音频时长（秒）'
        ),
      },

      constraints: {
        maxAudioDuration: 300, // 单段音频上限约 5 分钟（综合理解较长）
        supportedFormats: ['mp3', 'wav'],
        requiresTranscript: true, // JLPT 练习提供原文供学习
      },

      customValidator: (question) => {
        const errors = [];

        // JLPT 听力音频时长验证
        if (question.audioDuration) {
          // 注意：JSON 中可能存成字符串，这里统一转成 number 再比较
          const audioDuration = Number(question.audioDuration);
          if (!Number.isNaN(audioDuration)) {
            if (audioDuration > 300) {
              errors.push({
                field: 'audioDuration',
                message: 'JLPT 听力单段音频不能超过 5 分钟',
                code: 'JLPT_AUDIO_TOO_LONG',
              });
            }
            if (audioDuration < 5) {
              errors.push({
                field: 'audioDuration',
                message: 'JLPT 听力音频不能短于 5 秒',
                code: 'JLPT_AUDIO_TOO_SHORT',
              });
            }
          }
        }

        return errors;
      },
    }
  );

/**
 * 所有 JLPT 题目类型定义
 */
export const JLPT_QUESTION_TYPE_DEFINITIONS: Record<
  string,
  ExamSpecificQuestionTypeDefinition
> = {
  [jlptVocabularyDefinition.id]: jlptVocabularyDefinition,
  [jlptGrammarReadingDefinition.id]: jlptGrammarReadingDefinition,
  [jlptListeningDefinition.id]: jlptListeningDefinition,
};

/**
 * 根据基础类型获取 JLPT 题目类型定义
 */
export function getJLPTQuestionTypeDefinition(
  baseType: QuestionType
): ExamSpecificQuestionTypeDefinition | undefined {
  return Object.values(JLPT_QUESTION_TYPE_DEFINITIONS).find(
    (def) => def.baseType === baseType
  );
}

/**
 * 获取 JLPT 题目类型的 Section 建议
 * 根据题目类型推荐应该属于哪个 Section
 */
export function getJLPTSectionSuggestion(
  questionTypeId: string
): 'vocabulary' | 'grammar_reading' | 'listening' | null {
  const map: Record<string, 'vocabulary' | 'grammar_reading' | 'listening'> = {
    [jlptVocabularyDefinition.id]: 'vocabulary',
    [jlptGrammarReadingDefinition.id]: 'grammar_reading',
    [jlptListeningDefinition.id]: 'listening',
  };
  return map[questionTypeId] ?? null;
}
